/**
 * DocumentRequirementsAdminPage — STAS-only CRUD for the dynamic checklist.
 *
 * Simple table-based admin (NOT drag-drop). STAS can:
 *   • Add a new requirement (any type: file / text / email / phone / date / number / textarea)
 *   • Mark required / optional
 *   • Toggle which nationality / applicant_type it appears for
 *   • Pick applies_to (main / members / both)
 *   • Pick stage (docs / extras)
 *   • Map a smart_scan field (so OCR auto-fills it)
 *   • Reorder via the `order` number
 *   • Soft-delete (is_active = false)
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNum } from "@/lib/format";

const FIELD_TYPES = [
  { value: "file",     label: "ملف" },
  { value: "text",     label: "نص" },
  { value: "email",    label: "بريد إلكتروني" },
  { value: "phone",    label: "رقم جوال" },
  { value: "date",     label: "تاريخ" },
  { value: "number",   label: "رقم" },
  { value: "textarea", label: "نص طويل" },
];

const SMART_SCAN_OPTIONS = [
  { value: "",                label: "— لا ربط —" },
  { value: "full_name",       label: "الاسم الكامل" },
  { value: "passport_number", label: "رقم الجواز" },
  { value: "nationality",     label: "الجنسية" },
  { value: "date_of_birth",   label: "تاريخ الميلاد" },
  { value: "expiry_date",     label: "تاريخ الانتهاء" },
  { value: "sex",             label: "الجنس" },
  { value: "national_id",     label: "رقم الهوية / الإقامة" },
];

const APPLIES_TO_OPTIONS = [
  { value: "main",     label: "المتقدم الرئيسي فقط" },
  { value: "both",     label: "المتقدم الرئيسي + المرافقون" },
  { value: "members",  label: "المرافقون فقط" },
  { value: "selected", label: "أفراد مختارون (يحدّدهم الموظف لاحقاً)" },
];

const STAGE_OPTIONS = [
  { value: "basic",     label: "المعلومات الأساسية" },
  { value: "travelers", label: "بيانات المسافرين" },
  { value: "docs",      label: "رفع المستندات" },
  { value: "addons",    label: "الخدمات الإضافية" },
  { value: "policy",    label: "السياسة والإرسال" },
];

const NATIONALITY_OPTIONS = [
  { value: "saudi",     label: "مواطن" },
  { value: "non_saudi", label: "مقيم / أجنبي" },
];

const APPLICANT_OPTIONS = [
  { value: "individual", label: "فردي" },
  { value: "family",     label: "عائلة" },
];

// Lookup helpers — converts internal code → Arabic for display
const labelOf = (opts, val) => (opts.find((o) => o.value === val)?.label || val);
const natLabel = (arr) => {
  if (!arr || arr.length === 0) return "—";
  if (arr.length === NATIONALITY_OPTIONS.length) return "الجميع";
  return arr.map((v) => labelOf(NATIONALITY_OPTIONS, v)).join(" / ");
};

const BLANK = {
  key: "", label_ar: "", label_en: "", label_ur: "", type: "file", required: true,
  nationality: ["saudi", "non_saudi"], applicant_type: ["individual", "family"],
  applies_to: "main", stage: "docs", smart_scan_map: "", order: 100,
  is_active: true, helper_ar: "", helper_en: "", helper_ur: "", count: 1,
};

// Smart default for the "stage" field based on the chosen "type"
function defaultStageForType(type) {
  if (type === "file") return "docs";
  return "basic"; // text, email, phone, date, number, textarea → basic info
}

export default function DocumentRequirementsAdminPage() {
  const { theme, dir } = useI18n();
  const css = makeStyles(theme);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);  // null | "new" | <key>
  const [draft, setDraft] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const r = await api.get("/admin/document-requirements");
      setItems(r.data?.items || []);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setDraft(BLANK); setEditing("new"); };
  const startEdit = (it) => { setDraft({ ...BLANK, ...it, smart_scan_map: it.smart_scan_map || "" }); setEditing(it.key); };
  const cancel = () => { setEditing(null); setDraft(BLANK); };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...draft, smart_scan_map: draft.smart_scan_map || null };
      if (editing === "new") {
        await api.post("/admin/document-requirements", payload);
      } else {
        // PATCH excludes "key" (it is the URL segment)
        const { key, ...rest } = payload;
        await api.patch(`/admin/document-requirements/${editing}`, rest);
      }
      await load();
      cancel();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (key) => {
    if (!window.confirm("سيتم إخفاء المتطلب — هل تتابع؟")) return;
    try {
      await api.delete(`/admin/document-requirements/${key}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div dir={dir} style={css.page} data-testid="doc-reqs-page">
      <header style={css.header}>
        <div>
          <h1 style={css.h1}>إدارة متطلبات المستندات</h1>
          <p style={css.sub}>
            هذه القائمة تظهر للعميل في خطوة "رفع المستندات". أضف، عدّل، أو احذف
            أي متطلب بدون الحاجة لتعديل كود.
          </p>
        </div>
        <button onClick={startNew} style={css.primaryBtn} data-testid="add-req-btn">
          + متطلب جديد
        </button>
      </header>

      {err && <div style={css.err} data-testid="reqs-error">{err}</div>}

      {editing && (
        <RequirementForm
          draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel}
          isNew={editing === "new"} busy={busy} css={css} theme={theme}
        />
      )}

      <div style={css.card}>
        <div style={{ overflowX: "auto" }}>
          <table style={css.table}>
            <thead>
              <tr>
                <th>الترتيب</th>
                <th>المفتاح الداخلي</th>
                <th>العنوان</th>
                <th>نوع الحقل</th>
                <th>إلزامي</th>
                <th>يظهر لـ (الجنسية)</th>
                <th>يطبّق على</th>
                <th>مرحلة العرض</th>
                <th>Smart Scan</th>
                <th>الحالة</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.key} data-testid={`req-row-${it.key}`}
                    style={!it.is_active ? { opacity: 0.45 } : null}>
                  <td>{fmtNum(it.order)}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 11, opacity: 0.65 }}>{it.key}</td>
                  <td><strong>{it.label_ar}</strong></td>
                  <td><span style={css.chip}>{labelOf(FIELD_TYPES, it.type)}</span></td>
                  <td>{it.required ? "✔" : "—"}</td>
                  <td style={{ fontSize: 11 }}>{natLabel(it.nationality)}</td>
                  <td><span style={css.chip}>{labelOf(APPLIES_TO_OPTIONS, it.applies_to)}</span></td>
                  <td><span style={css.chip}>{labelOf(STAGE_OPTIONS, it.stage)}</span></td>
                  <td style={{ fontSize: 11, color: theme.gold }}>
                    {it.smart_scan_map ? labelOf(SMART_SCAN_OPTIONS, it.smart_scan_map) : "—"}
                  </td>
                  <td>{it.is_active ? "نشط" : "مُخفى"}</td>
                  <td>
                    <button onClick={() => startEdit(it)} style={css.actionBtn} data-testid={`edit-${it.key}`}>تعديل</button>
                    {it.is_active && (
                      <button onClick={() => remove(it.key)} style={{ ...css.actionBtn, color: "#dc2626" }} data-testid={`del-${it.key}`}>
                        إخفاء
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RequirementForm({ draft, setDraft, onSave, onCancel, isNew, busy, css, theme }) {
  const set = (k, v) => setDraft((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, val) => {
    const cur = draft[k] || [];
    set(k, cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
  };

  // Iteration 27: when the user picks a TYPE, suggest the most appropriate
  // stage automatically (file → docs, text/email/etc → basic).
  const setType = (newType) => {
    setDraft((s) => {
      // Only auto-switch the stage if the current stage is the previous
      // default for the old type — preserves any explicit STAS choice.
      const oldDefault = defaultStageForType(s.type);
      const nextStage = s.stage === oldDefault ? defaultStageForType(newType) : s.stage;
      return { ...s, type: newType, stage: nextStage };
    });
  };

  return (
    <div style={css.formCard} data-testid="req-form">
      <h3 style={css.h3}>{isNew ? "إضافة متطلب جديد" : `تعديل: ${draft.label_ar}`}</h3>
      <div style={css.grid2}>
        <Field label="المفتاح الداخلي (لا يمكن تغييره)" hint="مثال: qiwa_contract — للنظام فقط، لا يظهر للعميل">
          <input value={draft.key} onChange={(e) => set("key", e.target.value)}
            placeholder="qiwa_contract"
            disabled={!isNew} style={css.input} data-testid="req-key" />
        </Field>
        <Field label="الاسم الذي يراه العميل (عربي)">
          <input value={draft.label_ar} onChange={(e) => set("label_ar", e.target.value)}
            placeholder="مثل: عقد قوى"
            style={css.input} data-testid="req-label-ar" />
        </Field>
        <Field label="الاسم بالإنجليزية (اختياري)">
          <input value={draft.label_en} onChange={(e) => set("label_en", e.target.value)}
            style={css.input} data-testid="req-label-en" />
        </Field>
        <Field label="الاسم بالأردو (اختياري)">
          <input value={draft.label_ur || ""} onChange={(e) => set("label_ur", e.target.value)}
            dir="rtl" placeholder="پاسپورٹ کی کاپی"
            style={{ ...css.input, fontFamily: "'Noto Nastaliq Urdu', serif", fontSize: 16 }}
            data-testid="req-label-ur" />
        </Field>
        <Field label="نوع الحقل">
          <select value={draft.type} onChange={(e) => setType(e.target.value)}
            style={css.input} data-testid="req-type">
            {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="أين يظهر هذا المتطلب للعميل؟"
               hint="اختر المرحلة التي سيظهر فيها هذا المتطلب داخل نموذج العميل.">
          <select value={draft.stage} onChange={(e) => set("stage", e.target.value)}
            style={css.input} data-testid="req-stage">
            {STAGE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="يطبّق على">
          <select value={draft.applies_to} onChange={(e) => set("applies_to", e.target.value)}
            style={css.input} data-testid="req-applies-to">
            {APPLIES_TO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="إلزامي؟">
          <label style={css.toggle}>
            <input type="checkbox" checked={draft.required}
              onChange={(e) => set("required", e.target.checked)} data-testid="req-required" />
            <span>{draft.required ? "نعم — إلزامي" : "اختياري"}</span>
          </label>
        </Field>
        <Field label="الترتيب (الأقل = يظهر أولاً)">
          <input type="number" value={draft.order} onChange={(e) => set("order", Number(e.target.value))}
            min="0" max="9999" style={css.input} data-testid="req-order" />
        </Field>
        <Field label="عدد الملفات المطلوبة (يستخدم فقط مع الملفات)">
          <input type="number" value={draft.count} onChange={(e) => set("count", Number(e.target.value))}
            min="1" max="10" style={css.input} data-testid="req-count" />
        </Field>
        <Field label="ربط Smart Scan (اختياري — يعبّئ الحقل تلقائياً من الجواز/الهوية)">
          <select value={draft.smart_scan_map || ""} onChange={(e) => set("smart_scan_map", e.target.value)}
            style={css.input} data-testid="req-scan-map">
            <option value="">— لا ربط —</option>
            <option value="__full__">📄 قراءة كاملة (يعبّئ كل الحقول دفعة واحدة)</option>
            {SMART_SCAN_OPTIONS.filter(f => f.value).map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </Field>
        <Field label="إلزامية القراءة الذاتية">
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 0" }}>
            <input
              type="checkbox"
              checked={!!draft.mandatory_smart_scan}
              onChange={(e) => set("mandatory_smart_scan", e.target.checked)}
              data-testid="req-mandatory-scan"
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 13 }}>
              {draft.mandatory_smart_scan
                ? "إلزامي — العميل لا يقدر يكتب يدوياً، لازم يصوّر"
                : "اختياري — العميل يقدر يكتب أو يصوّر"}
            </span>
          </label>
        </Field>
      </div>

      <Field label="يظهر لـ (الجنسية)">
        <div style={css.checks}>
          {NATIONALITY_OPTIONS.map((n) => (
            <label key={n.value} style={css.checkLbl}>
              <input type="checkbox" checked={(draft.nationality || []).includes(n.value)}
                onChange={() => toggleArr("nationality", n.value)} data-testid={`req-nat-${n.value}`} />
              <span>{n.label}</span>
            </label>
          ))}
        </div>
      </Field>

      <Field label="يظهر لـ (نوع التقديم)">
        <div style={css.checks}>
          {APPLICANT_OPTIONS.map((n) => (
            <label key={n.value} style={css.checkLbl}>
              <input type="checkbox" checked={(draft.applicant_type || []).includes(n.value)}
                onChange={() => toggleArr("applicant_type", n.value)} data-testid={`req-appl-${n.value}`} />
              <span>{n.label}</span>
            </label>
          ))}
        </div>
      </Field>

      <Field label="ملاحظة مساعدة (عربي — اختياري)">
        <input value={draft.helper_ar || ""} onChange={(e) => set("helper_ar", e.target.value)}
          placeholder="مثل: خلفية بيضاء، عالية الوضوح"
          style={css.input} data-testid="req-helper" />
      </Field>
      <Field label="ملاحظة مساعدة (إنجليزي — اختياري)">
        <input value={draft.helper_en || ""} onChange={(e) => set("helper_en", e.target.value)}
          placeholder="e.g. White background, high clarity"
          style={css.input} data-testid="req-helper-en" />
      </Field>
      <Field label="ملاحظة مساعدة (اردو — اختياري)">
        <input value={draft.helper_ur || ""} onChange={(e) => set("helper_ur", e.target.value)}
          dir="rtl"
          placeholder="سفید پس منظر، اعلیٰ معیار"
          style={{ ...css.input, fontFamily: "'Noto Nastaliq Urdu', serif", fontSize: 15 }}
          data-testid="req-helper-ur" />
      </Field>

      <div style={css.formActions}>
        <button onClick={onCancel} disabled={busy} style={css.secondaryBtn} data-testid="cancel-form">إلغاء</button>
        <button onClick={onSave} disabled={busy || !draft.key || !draft.label_ar}
          style={{ ...css.primaryBtn, opacity: busy ? 0.5 : 1 }} data-testid="save-form">
          {busy ? "…" : "حفظ"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, marginBottom: 4, opacity: 0.8 }}>{label}</div>
      {hint && <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 4 }}>{hint}</div>}
      {children}
    </div>
  );
}

function makeStyles(theme) {
  return {
    page: { maxWidth: 1200, margin: "0 auto", padding: 16, color: theme.text },
    header: { display: "flex", justifyContent: "space-between", alignItems: "start", gap: 16, marginBottom: 16, flexWrap: "wrap" },
    h1: { fontSize: 22, fontWeight: 900, margin: 0, color: theme.gold },
    h3: { fontSize: 14, fontWeight: 800, marginTop: 0, marginBottom: 12, color: theme.gold },
    sub: { fontSize: 12, color: theme.textMuted, lineHeight: 1.7, maxWidth: 600 },
    primaryBtn: { padding: "8px 16px", borderRadius: 8, background: theme.gold, color: "#0a0a0a", fontWeight: 900, fontSize: 12, border: "none", cursor: "pointer" },
    secondaryBtn: { padding: "8px 16px", borderRadius: 8, background: "transparent", color: theme.text, border: `1px solid ${theme.borderSoft}`, cursor: "pointer", fontSize: 12 },
    actionBtn: { padding: "4px 10px", borderRadius: 6, background: "transparent", border: `1px solid ${theme.borderSoft}`, color: theme.text, fontSize: 11, cursor: "pointer", marginInlineStart: 4 },
    card: { padding: 16, borderRadius: 12, border: `1px solid ${theme.borderSoft}`, background: theme.surfaceSubtle },
    formCard: { padding: 16, borderRadius: 12, border: `2px solid ${theme.gold}`, background: theme.surface, marginBottom: 16 },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
    chip: { fontSize: 10, padding: "2px 6px", borderRadius: 999, background: theme.surface, border: `1px solid ${theme.borderSoft}` },
    grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 },
    input: { width: "100%", padding: "8px 10px", borderRadius: 6, background: theme.surface, color: theme.text, border: `1px solid ${theme.borderSoft}`, fontSize: 12, boxSizing: "border-box" },
    checks: { display: "flex", gap: 12, flexWrap: "wrap" },
    checkLbl: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" },
    toggle: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" },
    formActions: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 },
    err: { color: "#dc2626", padding: 10, background: "#fee2e2", borderRadius: 8, marginBottom: 12, fontSize: 12 },
  };
}
